import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Label,
} from 'recharts';

import { VARIABLES } from '@app/components/common/constants';
import { BaseForecastModel, FORECAST_MODELS } from '@app/common/static-data/models';
import { useTheme } from '@app/theme/ThemeContext';
import { PerformanceMetric } from '@app/api/live-benchmarking/types';

interface TimeSeriesChartProps {
  data: PerformanceMetric[];
  selectedVariable: string;
  selectedMetric: 'rmse' | 'mbe';
  leadTime: number; // in hours
}

export function TimeSeriesChart({
  data,
  selectedVariable,
  selectedMetric,
  leadTime,
}: TimeSeriesChartProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#E5E7EB' : '#666666';

  const variable = VARIABLES.find((v) => v.juaId === selectedVariable);
  if (!variable) return null;

  // Filter data for specific lead time
  const filteredData = data.filter((d) => d.prediction_timedelta === leadTime);
  // Sort data by valid_time
  const sortedData = [...filteredData].sort((a, b) => {
    return a.valid_time.getTime() - b.valid_time.getTime();
  });
  const availableModels = [...new Set(data.map((d) => d.model))].map((model) =>
    FORECAST_MODELS.find((m: BaseForecastModel) => m.id === model)!,
  );

  // Transform data for recharts
  const chartData = sortedData.reduce((acc: any[], d) => {
    const existingEntry = acc.find((item) => item.valid_time.getTime() === d.valid_time.getTime());

    if (existingEntry) {
      // Add the value for this model to existing entry
      existingEntry[`value_${d.model}`] = d[selectedVariable as keyof PerformanceMetric] as number;
      return acc;
    } else {
      // Create new entry
      return [
        ...acc,
        {
          valid_time: d.valid_time,
          [`value_${d.model}`]: d[selectedVariable as keyof PerformanceMetric] as number,
        },
      ];
    }
  }, []);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !variable) return null;

    return (
      <div
        className={`${isDark ? 'bg-theme-dark-bg border-theme-dark-sidebar' : 'bg-white border-gray-200'} p-3 border rounded-lg`}
      >
        {payload.map((entry: any, index: number) => {
          const modelId = entry.dataKey.replace('value_', '');
          const model = availableModels.find((m: BaseForecastModel) => m.id === modelId)!;

          return (
            <p
              key={index}
              className={`text-sm ${isDark ? 'text-theme-dark-text-primary' : 'text-gray-900'}`}
              style={{ color: entry.color }}
            >
              {`${model.label}: ${entry.value.toFixed(2)} ${variable.displayUnit}`}
            </p>
          );
        })}
      </div>
    );
  };

  const getModelColor = (model: BaseForecastModel, index: number, total: number) => {
    // Special colors for EPT models
    if (model.id.includes('ept')) {
      // For EPT models, use purple/pink hues (280-320)
      const hue = model.id.includes('early') ? 320 : 280; // pink for early, purple for regular
      return `hsl(${hue}, 70%, 45%)`;
    }

    // For other models, distribute colors evenly, avoiding purple/pink range
    const hue = (index * 240) / total; // Use 0-240 range instead of 0-360
    return `hsl(${hue}, 70%, 45%)`;
  };

  return (
    <div className="relative">
      <div className="overflow-x-auto min-h-[300px] -mx-2 sm:mx-0">
        <div className="min-w-[800px] h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 20, right: 75, left: 30, bottom: 20 }}>
              <XAxis
                dataKey="valid_time"
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    hour12: false,
                    timeZone: 'UTC',
                  });
                }}
                tick={{ fontSize: 12, fill: axisColor }}
              />

              <YAxis
                yAxisId="main"
                orientation="left"
                tickFormatter={(x) => x.toLocaleString()}
                tick={{ fontSize: 12, fill: axisColor }}
              >
                <Label
                  angle={270}
                  position="left"
                  style={{ textAnchor: 'middle', fill: axisColor }}
                  offset={15}
                >
                  {`${selectedMetric.toUpperCase()} [${variable.displayUnit}]`}
                </Label>
              </YAxis>

              <Tooltip content={CustomTooltip} />
              <Legend
                wrapperStyle={{
                  color: axisColor,
                }}
              />

              {availableModels.map((model, index) => (
                <Line
                  key={`value_${model.id}`}
                  type="monotone"
                  dataKey={`value_${model.id}`}
                  name={model.label}
                  yAxisId="main"
                  dot={false}
                  stroke={getModelColor(model, index, availableModels.length)}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
